import { Component, Input, OnDestroy } from "@angular/core";
import { GameType } from "@apiserver-rest/ponder-data-client-base";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { WindowResizeEvent } from "@ponder-core/events/ui.events";
import { gameTypeToGameInfo } from "@ponder-shared/utils/gameInfo";
import { isMobileScreen } from "@ponder-shared/utils/screenBreakpoints";
import { Subscription } from "rxjs";

@Component({
    selector: "navbar",
    imports: [],
    templateUrl: "./navbar.component.html"
})
export class NavbarComponent implements OnDestroy {
    @Input({ required: true }) minSegments!: number;

    public navbarSegments?: NavbarSegment[];
    public isMobileScreen: boolean = false;
    
    private navbarUpdateSubscription!: Subscription;
    private windowResizeSubscription!: Subscription;

    public constructor(private events: EventBroker) {
        this.navbarUpdateSubscription = this.events.ui.navbarUpdateEvent.subscribe({
            next: (navbarUpdate: NavbarSegment[]) => {
                this.refreshNavbar(navbarUpdate);
            }
        })

        this.windowResizeSubscription = this.events.ui.windowResizeEvent.subscribe({
            next: (windowResize: WindowResizeEvent) => {
                this.isMobileScreen = isMobileScreen();
            }
        })

        this.isMobileScreen = isMobileScreen();
    }

    public ngOnDestroy(): void {
        this.navbarUpdateSubscription.unsubscribe();
        this.windowResizeSubscription.unsubscribe();
    }

    private refreshNavbar(segments: NavbarSegment[]): void {
        this.navbarSegments = segments;
    }
    
    public getSegments(): NavbarSegment[] | undefined {
        if (this.isMobileScreen) {
            return this.navbarSegments?.slice(-this.minSegments);
        } else {
            return this.navbarSegments;
        }
    }
}

// -----------------------------------------------------------------------------------------
// NavbarSegment
// -----------------------------------------------------------------------------------------

export class NavbarSegment {
    public readonly index!: string;

    public constructor(public name: string, public shortName: string, public url?: string) { 
        this.index = `${name}:${url}`;
    }
}

/**
 * Builds the default base navbar segment (All)
 */
export function buildBaseSegment(): NavbarSegment {
    return new NavbarSegment("All", "All", "/games");
}

/**
 * Builds the default navbar segment for the specified game.
 */
export function buildGameSegment(game: GameType): NavbarSegment | undefined {
    var gameInfo = gameTypeToGameInfo(game);

    if (!gameInfo) {
        return undefined;
    }
    
    return new NavbarSegment(gameInfo.shortName, gameInfo.shortName, `/${gameInfo.shortName.toLowerCase()}`);
}

/**
 * Optionally adds a new segment to the specified list. If name or shortName are undefined, the segment is not added.
 */
export function addSegmentToList(segments: NavbarSegment[], name?: string, shortName?: string, url?: string): void {
    if (!name || !shortName) {
        return;
    }

    segments.push(new NavbarSegment(name, shortName, url));
}

/**
 * Builds the initial navbar segment array with the base and game segments.
 * @param game
 */
export function intializeBaseSegmentArray(game: GameType): NavbarSegment[] | undefined {
    var baseSegment = buildBaseSegment();
    var gameSegment = buildGameSegment(game);

    if (!gameSegment) {
        return undefined;
    }

    return [baseSegment, gameSegment];
}

export function initializeUserSegmentArray(username: string): NavbarSegment[] {
    var segmentList = [ 
        new NavbarSegment("User", "User"),
        new NavbarSegment(username, username, `/user/${username}`),
    ];

    return segmentList;
}